/**
 * ZOOM-08 — formatacao pura da lista e da sala de aulas ao vivo.
 * Horario sempre no fuso da aula (`timezone`, hoje fixo em America/Sao_Paulo, D8), nunca no
 * fuso do navegador: o aluno em outro fuso ve o mesmo horario que o admin agendou.
 */
import type { LiveSession } from '@/types/api'

const LOCALE = 'pt-BR'
const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600
const SECONDS_PER_DAY = 86400

function format(iso: string, timeZone: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(LOCALE, { ...options, timeZone }).format(new Date(iso))
}

/** "10/11/2026" */
export function formatSessionDate(iso: string, timeZone: string): string {
  return format(iso, timeZone, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** "20:00" */
export function formatSessionTime(iso: string, timeZone: string): string {
  return format(iso, timeZone, { hour: '2-digit', minute: '2-digit', hour12: false })
}

/** "10/11/2026, 20:00 às 21:30" */
export function formatSessionSchedule(session: Pick<LiveSession, 'starts_at' | 'ends_at' | 'timezone'>): string {
  const { starts_at: startsAt, ends_at: endsAt, timezone } = session
  return `${formatSessionDate(startsAt, timezone)}, ${formatSessionTime(startsAt, timezone)} às ${formatSessionTime(endsAt, timezone)}`
}

/** "10/11/2026 às 19:45" */
export function formatSessionMoment(iso: string, timeZone: string): string {
  return `${formatSessionDate(iso, timeZone)} às ${formatSessionTime(iso, timeZone)}`
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Contagem regressiva legivel: "2 d 03:04:05", "03:04:05" ou "04:05". Negativo vira "00:00". */
export function formatCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.ceil(remainingMs / MS_PER_SECOND))
  const days = Math.floor(total / SECONDS_PER_DAY)
  const hours = Math.floor((total % SECONDS_PER_DAY) / SECONDS_PER_HOUR)
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)
  const seconds = total % SECONDS_PER_MINUTE
  const clock = hours > 0 || days > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`
  return days > 0 ? `${days} d ${clock}` : clock
}

/** Rota da sala a partir do prefixo da lista (contrato §4.4). */
export function liveSessionRoomPath(basePath: string, id: number): string {
  return `${basePath}/${id}`
}

export function panelistNames(session: Pick<LiveSession, 'panelists'>): string {
  return session.panelists.map((person) => person.name).join(', ')
}

/**
 * R10-10 — sala aberta, mas o host ainda nao iniciou o webinar (o webhook `webinar.started`
 * ainda nao chegou). So vale para quem nao e o host: o host e quem inicia.
 */
export function isWaitingForHost(session: Pick<LiveSession, 'status' | 'my_role' | 'can_join'>): boolean {
  return session.can_join && session.status === 'scheduled' && session.my_role !== 'host'
}
